import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useExamSession } from "@/contexts/ExamSessionContext";
import { deriveProgramWindowFromEnrollment } from "@/lib/institutional-enrollment";
import { getActiveEnrollmentCached, activeEnrollmentQueryKey } from "./useActiveEnrollment";
import { trackEvent } from "@/lib/analytics";

export interface InstitutionalCohortInfo {
  cohort_id: string;
  school_id: string;
  start_at: string;
  end_at: string;
  name: string;
  min_weekly_levels: number;
  enrolled_at: string;
}

export interface BaselineInfo {
  attempt_id: string;
  score: number;
  completed_at: string;
  passed: boolean;
}

export interface FinalExamInfo {
  attempt_id: string;
  score: number;
  completed_at: string;
  passed: boolean;
}

export interface WeeklyComplianceInfo {
  levels_completed: number;
  real_exam_done: boolean;
  is_compliant: boolean;
  week_start: string;
  week_end: string;
  week_number: number;
}

export type StatusReliability = 'confirmed' | 'degraded' | 'unknown';

export interface InstitutionalStudentStatus {
  isInstitutionalStudent: boolean;
  cohort: InstitutionalCohortInfo | null;
  baseline: BaselineInfo | null;
  hasBaseline: boolean;
  finalExam: FinalExamInfo | null;
  hasFinalExam: boolean;
  currentWeek: number;
  isLastWeek: boolean;
  currentWeekCompliance: WeeklyComplianceInfo | null;
  previousWeekCompliance: WeeklyComplianceInfo | null;
  isBlockedByCompliance: boolean;
  /** true if user must complete baseline before accessing levels */
  isBaselineRequired: boolean;
  totalProgramWeeks: number;
  /** true if current date falls within the dynamic grace period — no compliance required */
  isGraceWeek: boolean;
  /** Number of grace days (10% of cohort duration, min 1, max 7) */
  graceDays: number;
  /** true if current date is before cohort.start_at — all academic activity blocked */
  isProgramNotStarted: boolean;
  /** true if user account was created by the institution (no prior personal account) */
  wasCreatedByInstitution: boolean;
  /** true if cohort has ended (end_at < now) */
  cohortEnded: boolean;
  /** true if institutional program is currently active (started and not ended) */
  isProgramActive: boolean;
  /** Indicates data reliability: 'confirmed' = all RPCs succeeded, 'degraded' = active enrollment confirmed but details failed, 'unknown' = nothing loaded */
  statusReliability: StatusReliability;
  /** Number of past weeks where the student did not meet compliance */
  pendingWeeksCount: number;
  /** First week number where non-compliance was detected (0 = none) */
  firstPendingWeek: number;
}

const DEFAULT_STATUS: InstitutionalStudentStatus = {
  isInstitutionalStudent: false,
  cohort: null,
  baseline: null,
  hasBaseline: false,
  finalExam: null,
  hasFinalExam: false,
  currentWeek: 0,
  isLastWeek: false,
  currentWeekCompliance: null,
  previousWeekCompliance: null,
  isBlockedByCompliance: false,
  isBaselineRequired: false,
  totalProgramWeeks: 0,
  isGraceWeek: false,
  graceDays: 0,
  isProgramNotStarted: false,
  wasCreatedByInstitution: false,
  cohortEnded: false,
  isProgramActive: false,
  statusReliability: 'unknown',
  pendingWeeksCount: 0,
  firstPendingWeek: 0,
};

/** Degraded fallback: we KNOW the user still has an active institutional enrollment but details failed */
const DEGRADED_INSTITUTIONAL_STATUS: InstitutionalStudentStatus = {
  ...DEFAULT_STATUS,
  isInstitutionalStudent: true,
  isProgramActive: true,
  isBlockedByCompliance: false,
  isBaselineRequired: false,
  isProgramNotStarted: false,
  statusReliability: 'degraded',
};

/** Compute dynamic grace days: 10% of cohort duration, min 1, max 7 */
function computeGraceDays(startAt: string, endAt: string): number {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const durationDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  return Math.min(7, Math.max(1, Math.round(durationDays * 0.10)));
}

/**
 * Hook to manage institutional student mode rules:
 * - Institutional status is DERIVED from active cohort enrollment, never from an orphaned role
 * - Requires baseline Real Exam before unlocking levels
 * - Weekly compliance: 2 quiz levels + 1 Real Exam per calendar week
 * - Blocks new level unlocks if previous week was non-compliant
 * - Detects final exam week based on cohort.end_at
 */
const INSTITUTIONAL_QUERY_TIMEOUT = 12000; // 12s max for all RPCs

/** Wrap a thenable/promise with a timeout that rejects after ms */
function withTimeout<T>(thenable: PromiseLike<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    Promise.resolve(thenable),
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`⏱️ [Institutional] ${label} timed out after ${ms}ms`)), ms)
    ),
  ]);
}

/** Detect the user's IANA timezone, fallback to America/New_York */
function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

export function useInstitutionalStudent(): InstitutionalStudentStatus & { loading: boolean } {
  const { user } = useAuth();
  const userTimezone = getUserTimezone();
  const queryClient = useQueryClient();
  const { examInProgress } = useExamSession();

  // Realtime removed for user_roles, institutional_student_cohorts, real_exam_attempts
  // (security: tables removed from supabase_realtime publication to prevent data leaks)
  // Revalidation now relies on visibilitychange fallback below + react-query staleTime

  // Foreground revalidation: throttled 30s + skipped during active exam
  const lastForegroundInvalidateRef = useRef<number>(0);
  const FOREGROUND_THROTTLE_MS = 30_000;
  useEffect(() => {
    if (!user?.id) return;

    const handleVisibility = () => {
      if (document.hidden) return;
      if (examInProgress) {
        console.log('🛡️ [Institutional] Exam in progress — skipping foreground invalidate');
        return;
      }
      const now = Date.now();
      if (now - lastForegroundInvalidateRef.current < FOREGROUND_THROTTLE_MS) {
        console.log('⏱️ [Institutional] Foreground invalidate throttled');
        return;
      }
      lastForegroundInvalidateRef.current = now;
      console.log('👁️ [Institutional] App foregrounded — invalidating');
      // Also drop shared enrollment cache so SubscriptionContext sees fresh data on next read
      queryClient.invalidateQueries({ queryKey: activeEnrollmentQueryKey(user.id) });
      queryClient.invalidateQueries({ queryKey: ["institutionalStudent", user.id] });
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user?.id, queryClient, examInProgress]);

  const { data, isPending, isFetching } = useQuery({
    queryKey: ["institutionalStudent", user?.id, userTimezone],
    queryFn: async (): Promise<InstitutionalStudentStatus> => {
      if (!user) return { ...DEFAULT_STATUS };

      const startTime = Date.now();
      console.log("🏫 [Institutional] Starting query for user:", user.id);

      let activeEnrollmentConfirmed = false;

      // ─────────────────────────────────────────────────────────────
      // P1: Single-trip bundle RPC. Falls back to the legacy multi-RPC
      // path on error/timeout so we never lock the user out during rollout.
      // ─────────────────────────────────────────────────────────────
      try {
        const bundleRes = await withTimeout(
          Promise.resolve(supabase.rpc("get_institutional_dashboard_bundle" as any, { p_timezone: userTimezone })),
          6000,
          "dashboard_bundle"
        );

        if (bundleRes.error) throw bundleRes.error;

        const bundle: any = bundleRes.data ?? {};
        const enr = bundle.enrollment;
        console.log("🏫 [Institutional] bundle:", {
          userId: user.id,
          hasEnrollment: !!enr,
          elapsed: Date.now() - startTime,
        });

        if (!enr) {
          const regularStatus = { ...DEFAULT_STATUS, statusReliability: 'confirmed' as const };
          console.log("🏫 [Institutional] DONE in", Date.now() - startTime, "ms (no enrollment)");
          return regularStatus;
        }

        activeEnrollmentConfirmed = true;

        // Refresh shared cache so SubscriptionContext stays consistent
        try {
          queryClient.setQueryData(activeEnrollmentQueryKey(user.id), enr);
        } catch {}

        const wasCreatedByInstitution = enr.created_by_institution ?? false;
        const cohort: InstitutionalCohortInfo = {
          cohort_id: enr.cohort_id,
          school_id: enr.school_id,
          start_at: enr.start_at,
          end_at: enr.end_at,
          name: enr.name,
          min_weekly_levels: enr.min_weekly_levels ?? 3,
          enrolled_at: enr.enrolled_at ?? enr.start_at,
        };

        const now = new Date();
        const { isProgramNotStarted, cohortEnded, isProgramActive } = deriveProgramWindowFromEnrollment(enr, now);

        const baselineRow = bundle.baseline ?? null;
        const baseline: BaselineInfo | null = baselineRow ? {
          attempt_id: baselineRow.attempt_id,
          score: baselineRow.score ?? 0,
          completed_at: baselineRow.completed_at,
          passed: baselineRow.passed ?? false,
        } : null;

        const finalExamRow = bundle.final_exam ?? null;
        const finalExam: FinalExamInfo | null = finalExamRow ? {
          attempt_id: finalExamRow.attempt_id,
          score: finalExamRow.score ?? 0,
          completed_at: finalExamRow.completed_at,
          passed: finalExamRow.passed ?? false,
        } : null;

        const currentWeek = Math.max(1, bundle.current_week ?? 1);

        const cohortEnd = new Date(cohort.end_at);
        const cohortStart = new Date(cohort.start_at);
        const msPerWeek = 7 * 24 * 60 * 60 * 1000;
        const totalProgramWeeks = Math.max(1, Math.ceil((cohortEnd.getTime() - cohortStart.getTime()) / msPerWeek));
        const isLastWeek = currentWeek >= totalProgramWeeks;

        const graceDays = computeGraceDays(cohort.start_at, cohort.end_at);
        const effectiveStart = new Date(Math.max(
          new Date(cohort.start_at).getTime(),
          new Date(cohort.enrolled_at).getTime()
        ));
        const graceEnd = new Date(effectiveStart.getTime() + graceDays * 24 * 60 * 60 * 1000);
        const isGraceWeek = now < graceEnd;

        const cohortStartDate = new Date(cohort.start_at);
        const prevWeekOffset = (currentWeek - 2) * 7 * 24 * 60 * 60 * 1000;
        const prevWeekStart = new Date(cohortStartDate.getTime() + prevWeekOffset);

        const currentRaw = bundle.current_week_compliance ?? null;
        const currentWeekCompliance: WeeklyComplianceInfo | null = currentRaw
          ? { ...currentRaw, week_number: currentWeek }
          : null;

        const prevRaw = bundle.previous_week_compliance ?? null;
        let previousWeekCompliance: WeeklyComplianceInfo | null = prevRaw
          ? { ...prevRaw, week_number: currentWeek - 1 }
          : null;

        // Weekly compliance gate retired 2026-07 — compliance data is kept in
        // the bundle for admin reports but never derives an access block.
        const isBlockedByCompliance = false;


        const pendingWeeksCount = bundle.pending_count ?? 0;
        const firstPendingWeek = bundle.first_pending_week ?? 0;

        console.log("🏫 [Institutional] DONE in", Date.now() - startTime, "ms (bundle path)");

        const BASELINE_GATE_ENABLED = false;
        const derivedStatus: InstitutionalStudentStatus = {
          isInstitutionalStudent: true,
          cohort,
          baseline,
          hasBaseline: BASELINE_GATE_ENABLED ? !!baseline : true,
          finalExam,
          hasFinalExam: !!finalExam,
          currentWeek,
          isLastWeek,
          currentWeekCompliance,
          previousWeekCompliance,
          isBlockedByCompliance,
          isBaselineRequired: BASELINE_GATE_ENABLED ? !baseline : false,
          totalProgramWeeks,
          isGraceWeek,
          graceDays,
          isProgramNotStarted,
          wasCreatedByInstitution,
          cohortEnded,
          isProgramActive,
          pendingWeeksCount,
          firstPendingWeek,
          statusReliability: 'confirmed',
        };
        return derivedStatus;
      } catch (bundleError) {
        console.warn("🏫 [Institutional] Bundle RPC failed — falling back to multi-RPC path:", bundleError);
      }

      // ─── LEGACY FALLBACK PATH ───
      try {
        // 1. Active enrollment (shared cache)
        const activeEnrollment = await withTimeout(
          getActiveEnrollmentCached(queryClient, user.id),
          5000,
          "active_enrollment_rpc"
        );

        if (!activeEnrollment) {
          return { ...DEFAULT_STATUS, statusReliability: 'confirmed' as const };
        }

        activeEnrollmentConfirmed = true;

        const wasCreatedByInstitution = activeEnrollment.created_by_institution ?? false;
        const cohort: InstitutionalCohortInfo = {
          cohort_id: activeEnrollment.cohort_id,
          school_id: activeEnrollment.school_id,
          start_at: activeEnrollment.start_at,
          end_at: activeEnrollment.end_at,
          name: activeEnrollment.name,
          min_weekly_levels: activeEnrollment.min_weekly_levels ?? 3,
          enrolled_at: activeEnrollment.enrolled_at ?? activeEnrollment.start_at,
        };

        const now = new Date();
        const { isProgramNotStarted, cohortEnded, isProgramActive } = deriveProgramWindowFromEnrollment(activeEnrollment, now);

        const [baselineResult, weekResult, finalExamResult] = await withTimeout(
          Promise.all([
            supabase.rpc("get_student_baseline" as any, { p_user_id: user.id }),
            supabase.rpc("get_current_program_week" as any, { p_cohort_start_at: cohort.start_at, p_timezone: userTimezone }),
            supabase.rpc("get_student_final_exam" as any, { p_user_id: user.id }),
          ]),
          6000,
          "baseline+week+final"
        );

        if (baselineResult.error) throw baselineResult.error;
        if (weekResult.error) throw weekResult.error;
        if (finalExamResult.error) {
          console.warn("🏫 [Institutional] get_student_final_exam failed:", finalExamResult.error);
        }

        const baselineRow = baselineResult.data?.[0] ?? null;
        const baseline: BaselineInfo | null = baselineRow ? {
          attempt_id: baselineRow.attempt_id,
          score: baselineRow.score ?? 0,
          completed_at: baselineRow.completed_at,
          passed: baselineRow.passed ?? false,
        } : null;

        const finalExamRow = finalExamResult.data?.[0] ?? null;
        const finalExam: FinalExamInfo | null = finalExamRow ? {
          attempt_id: finalExamRow.attempt_id,
          score: finalExamRow.score ?? 0,
          completed_at: finalExamRow.completed_at,
          passed: finalExamRow.passed ?? false,
        } : null;

        const currentWeek = Math.max(1, weekResult.data ?? 1);
        const cohortEnd = new Date(cohort.end_at);
        const cohortStart = new Date(cohort.start_at);
        const msPerWeek = 7 * 24 * 60 * 60 * 1000;
        const totalProgramWeeks = Math.max(1, Math.ceil((cohortEnd.getTime() - cohortStart.getTime()) / msPerWeek));
        const isLastWeek = currentWeek >= totalProgramWeeks;

        const graceDays = computeGraceDays(cohort.start_at, cohort.end_at);
        const effectiveStart = new Date(Math.max(
          new Date(cohort.start_at).getTime(),
          new Date(cohort.enrolled_at).getTime()
        ));
        const graceEnd = new Date(effectiveStart.getTime() + graceDays * 24 * 60 * 60 * 1000);
        const isGraceWeek = now < graceEnd;

        let currentWeekCompliance: WeeklyComplianceInfo | null = null;
        let previousWeekCompliance: WeeklyComplianceInfo | null = null;
        // Weekly compliance gate retired 2026-07 — data below is informational.
        const isBlockedByCompliance = false;
        let pendingWeeksCount = 0;
        let firstPendingWeek = 0;

        const cohortStartDate = new Date(cohort.start_at);
        const prevWeekOffset = (currentWeek - 2) * 7 * 24 * 60 * 60 * 1000;
        const prevWeekStart = new Date(cohortStartDate.getTime() + prevWeekOffset);
        const prevWeekWasGrace = currentWeek <= 1 || prevWeekStart < graceEnd;

        const needPrevCompliance = currentWeek >= 2 && !prevWeekWasGrace;
        const needPendingWeeks = currentWeek >= 2 && !isGraceWeek;

        const calls: Array<{ key: 'current' | 'prev' | 'pending'; promise: Promise<any> }> = [];
        calls.push({
          key: 'current',
          promise: Promise.resolve(supabase.rpc("check_weekly_compliance" as any, {
            p_user_id: user.id, p_week_number: currentWeek,
            p_cohort_start_at: cohort.start_at, p_cohort_id: cohort.cohort_id, p_timezone: userTimezone,
          })),
        });
        if (needPrevCompliance) {
          calls.push({
            key: 'prev',
            promise: Promise.resolve(supabase.rpc("check_weekly_compliance" as any, {
              p_user_id: user.id, p_week_number: currentWeek - 1,
              p_cohort_start_at: cohort.start_at, p_cohort_id: cohort.cohort_id, p_timezone: userTimezone,
            })),
          });
        }
        if (needPendingWeeks) {
          calls.push({
            key: 'pending',
            promise: Promise.resolve(supabase.rpc("count_pending_compliance_weeks" as any, {
              p_user_id: user.id, p_cohort_id: cohort.cohort_id,
              p_cohort_start_at: cohort.start_at, p_current_week: currentWeek,
              p_min_weekly_levels: cohort.min_weekly_levels, p_grace_days: graceDays, p_timezone: userTimezone,
            })).catch((e) => ({ data: null, error: e })),
          });
        }

        const settled = await withTimeout(Promise.all(calls.map(c => c.promise)), 6000, "compliance+pending");
        calls.forEach((c, i) => {
          const res = settled[i];
          if (c.key === 'current') {
            if (res?.error) throw res.error;
            const currentComp = res?.data?.[0] ?? null;
            currentWeekCompliance = currentComp ? { ...currentComp, week_number: currentWeek } : null;
          } else if (c.key === 'prev') {
            if (res?.error) throw res.error;
            const prevComp = res?.data?.[0] ?? null;
            if (prevComp) {
              previousWeekCompliance = { ...prevComp, week_number: currentWeek - 1 };
              // Weekly compliance is informational only (gate retired 2026-07).
            }
          } else if (c.key === 'pending') {
            const row = res?.data?.[0];
            if (row) {
              pendingWeeksCount = row.pending_count ?? 0;
              firstPendingWeek = row.first_pending_week ?? 0;
            }
          }
        });


        console.log("🏫 [Institutional] DONE in", Date.now() - startTime, "ms (legacy path)");


        // ─── PRODUCT DECISION (2026-06): Baseline gate disabled for the new
        // institutional flow. The roadmap is now: enter cohort → practice L1 →
        // pass L1 in all 7 modules → unlock Real Exam → pass Real Exam →
        // unlock L2 (handled by useInstitutionalProgression).
        //
        // We intentionally short-circuit `isBaselineRequired` to false (and
        // mark `hasBaseline` true) so downstream guards — access-engine,
        // institutional-access route guard, LevelSelector, QuizLevel, dashboard
        // banner and ActionButtons — fall through to non-baseline behavior
        // without touching each call site. The baseline data itself is kept
        // for compatibility / future reactivation.
        const BASELINE_GATE_ENABLED = false;

        const derivedStatus: InstitutionalStudentStatus = {
          isInstitutionalStudent: true,
          cohort,
          baseline,
          hasBaseline: BASELINE_GATE_ENABLED ? !!baseline : true,
          finalExam,
          hasFinalExam: !!finalExam,
          currentWeek,
          isLastWeek,
          currentWeekCompliance,
          previousWeekCompliance,
          isBlockedByCompliance,
          isBaselineRequired: BASELINE_GATE_ENABLED ? !baseline : false,
          totalProgramWeeks,
          isGraceWeek,
          graceDays,
          isProgramNotStarted,
          wasCreatedByInstitution,
          cohortEnded,
          isProgramActive,
          pendingWeeksCount,
          firstPendingWeek,
          statusReliability: 'confirmed',
        };

        console.log("🏫 [Institutional] Derived status:", {
          userId: user.id,
          reason: "active_enrollment_confirmed",
          isInstitutionalStudent: derivedStatus.isInstitutionalStudent,
          isProgramActive: derivedStatus.isProgramActive,
          isProgramNotStarted: derivedStatus.isProgramNotStarted,
          cohortEnded: derivedStatus.cohortEnded,
          isBaselineRequired: derivedStatus.isBaselineRequired,
          hasBaseline: derivedStatus.hasBaseline,
          statusReliability: derivedStatus.statusReliability,
          startAt: derivedStatus.cohort?.start_at ?? null,
          endAt: derivedStatus.cohort?.end_at ?? null,
        });

        return derivedStatus;
      } catch (error) {
        console.error("🏫 [Institutional] Query failed/timed out after", Date.now() - startTime, "ms:", error);
        if (activeEnrollmentConfirmed) {
          // Active enrollment was confirmed but subsequent RPCs failed — return degraded (don't lock out)
          console.warn("🏫 [Institutional] Returning DEGRADED status — active enrollment confirmed, details unavailable");
          const degradedStatus = { ...DEGRADED_INSTITUTIONAL_STATUS };
          console.log("🏫 [Institutional] Derived status:", {
            userId: user.id,
            reason: "degraded_fallback_after_detail_failure",
            isInstitutionalStudent: degradedStatus.isInstitutionalStudent,
            isProgramActive: degradedStatus.isProgramActive,
            isProgramNotStarted: degradedStatus.isProgramNotStarted,
            cohortEnded: degradedStatus.cohortEnded,
            isBaselineRequired: degradedStatus.isBaselineRequired,
            hasBaseline: degradedStatus.hasBaseline,
            statusReliability: degradedStatus.statusReliability,
          });
          return degradedStatus;
        }
        // Could not confirm active enrollment — treat as regular user instead of persisting stale institutional state
        const regularStatus = { ...DEFAULT_STATUS, statusReliability: 'confirmed' as const };
        console.log("🏫 [Institutional] Derived status:", {
          userId: user.id,
          reason: "enrollment_not_confirmed_after_query_failure",
          isInstitutionalStudent: regularStatus.isInstitutionalStudent,
          isProgramActive: regularStatus.isProgramActive,
          isProgramNotStarted: regularStatus.isProgramNotStarted,
          cohortEnded: regularStatus.cohortEnded,
          statusReliability: regularStatus.statusReliability,
        });
        return regularStatus;
      }
    },
    enabled: !!user,
    staleTime: 60_000, // 60s — dedup foreground/mount bursts
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false, // explicit visibility listener above handles foreground refresh
    refetchOnMount: true, // honors staleTime — refetches only when data is older than 60s
    retry: 1,
    retryDelay: 2000,
  });

  // ─── Fase 4: Analytics events for baseline + compliance state transitions ───
  // Fire-and-forget, deduped per-session (ref) and per-state (localStorage).
  // Does NOT affect access logic or UI behavior.
  const emittedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!user?.id || !data || data.statusReliability !== 'confirmed') return;
    if (!data.isInstitutionalStudent) return;

    try {
      const uid = user.id;
      const cohortId = data.cohort?.cohort_id ?? 'unknown';

      // 1) baseline_required — emit once while baseline is pending
      if (data.isBaselineRequired && !data.hasBaseline) {
        const key = `analytics:baseline_required:${uid}:${cohortId}`;
        if (!emittedRef.current.has(key) && !localStorage.getItem(key)) {
          emittedRef.current.add(key);
          localStorage.setItem(key, String(Date.now()));
          trackEvent('baseline_required', 'institutional_student', {
            cohort_id: cohortId,
            current_week: data.currentWeek,
            is_program_active: data.isProgramActive,
          });
        }
      }

      // 2) baseline_completed — emit once per attempt_id
      if (data.hasBaseline && data.baseline?.attempt_id) {
        const key = `analytics:baseline_completed:${uid}:${data.baseline.attempt_id}`;
        if (!emittedRef.current.has(key) && !localStorage.getItem(key)) {
          emittedRef.current.add(key);
          localStorage.setItem(key, String(Date.now()));
          // Clear the pending baseline_required marker (next cohort can re-fire)
          try { localStorage.removeItem(`analytics:baseline_required:${uid}:${cohortId}`); } catch {}
          trackEvent('baseline_completed', 'institutional_student', {
            attempt_id: data.baseline.attempt_id,
            score: data.baseline.score,
            passed: data.baseline.passed,
            cohort_id: cohortId,
            current_week: data.currentWeek,
          });
        }
      }

      // 3) compliance_unlocked — RETIRED 2026-07
      // Removed together with the weekly-compliance access gate. isBlockedByCompliance
      // is now always false, so there is no real blocked→unlocked transition to emit.
      // Historical compliance data remains available via currentWeekCompliance /
      // previousWeekCompliance / pendingWeeks for admin reporting only.
    } catch {
      // Silent — analytics must never break the app
    }
  }, [user?.id, data]);

  return {
    ...(data ?? DEFAULT_STATUS),
    loading: !!user && (isPending || (!data && isFetching)),
  };
}
